/**
 * Live entitlement refresh (docs/PERMISSIONS.md Phase 2). When staff change an organization's features
 * (worker/src/dashboard-staff-orgs.js), OrgRoom pushes `{ t: "entitlements.changed" }` on `org:{orgId}`. Open
 * dashboards refetch /api/dashboard/me (the principal's featureMap: entitlements with overrides, and the role), hand
 * the map to navConfig (setViewerFeatures, which `can()` reads) and bump `version`, which the app shell and the
 * sidebars subscribe to, so the nav and the gated views re-render without a reload. A delta that carries `features`
 * is applied as is (no fetch).
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { setViewerFeatures, type FeatureMap, type OrgRole, type UserGroup } from "../nav/navConfig";

export interface EntitlementsState {
  group: UserGroup | null;
  role: OrgRole | null;
  features: FeatureMap | null;
  version: number;          // bumped on every change that reached navConfig
}

export type EntitlementsDelta = { t: "entitlements.changed"; features?: FeatureMap };

export const entitlementsStore = createStore<EntitlementsState>()(() => ({ group: null, role: null, features: null, version: 0 }));
export const useFeaturesVersion = () => useStore(entitlementsStore, (s) => s.version);

/** The page's first map (snapshot:me.features). */
export function initViewerFeatures(group: UserGroup | null, role: OrgRole | null, features: FeatureMap | null | undefined) {
  setViewerFeatures(group, role, features);
  entitlementsStore.setState({ group, role: role ?? null, features: group && features ? { ...features } : null });
}

const same = (a: FeatureMap | null, b: FeatureMap) => !!a && Object.keys(a).length === Object.keys(b).length && Object.entries(b).every(([k, v]) => a[k as keyof FeatureMap] === v);

/**
 * A new map from the server → navConfig and the store. Keys it doesn't name keep their old value. → whether anything
 * changed (no group, i.e. no dashboard access, or no map: nothing to do).
 */
export function applyViewerFeatures(features: FeatureMap | null | undefined): boolean {
  const s = entitlementsStore.getState();
  if (!s.group || !features || typeof features !== "object") return false;
  const next: FeatureMap = { ...(s.features ?? {}) };
  for (const [k, v] of Object.entries(features)) if (typeof v === "boolean") next[k as keyof FeatureMap] = v;
  if (same(s.features, next)) return false;
  setViewerFeatures(s.group, s.role, next);
  entitlementsStore.setState({ features: next, version: s.version + 1 });
  return true;
}

/** GET /api/dashboard/me → its features. → whether anything changed (a failed fetch changes nothing). */
export async function refreshViewerFeatures(get: typeof fetch = fetch): Promise<boolean> {
  const res = await get("/api/dashboard/me", { credentials: "include" }).catch(() => null);
  if (!res?.ok) return false;
  const body = (await res.json().catch(() => null)) as { features?: FeatureMap } | null;
  return applyViewerFeatures(body?.features);
}

/** An org-channel delta: `entitlements.changed` refreshes the map; anything else → null (not ours). */
export function onEntitlementsDelta(d: { t: string }, get: typeof fetch = fetch): Promise<boolean> | null {
  if (d.t !== "entitlements.changed") return null;
  const pushed = (d as EntitlementsDelta).features;
  return pushed ? Promise.resolve(applyViewerFeatures(pushed)) : refreshViewerFeatures(get);
}
